import { createContext } from 'react'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import {
  ApiError,
  clearApiSession,
  login as loginRequest,
  logout as logoutRequest,
  refreshSession,
  type AuthSession,
} from '../api/auth'
import type { AuthUser } from '../data/types'
import { navigate } from '../router/hash'

type LoginResult = { ok: true } | { ok: false; error: string }

type AuthContextValue = {
  user: AuthUser | null
  initializing: boolean
  login: (email: string, password: string) => Promise<LoginResult>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

function toAuthUser(session: AuthSession): AuthUser {
  return {
    ...session.user,
    username: session.user.email,
    activeFacilityId: session.activeFacilityId,
  }
}

function loginError(error: unknown): string {
  if (error instanceof ApiError) return error.message
  if (error instanceof TypeError) return 'Cannot connect to the API. Check that the backend is running.'
  return 'Unable to sign in. Please try again.'
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [initializing, setInitializing] = useState(true)

  useEffect(() => {
    let mounted = true
    refreshSession()
      .then((session) => {
        if (mounted) setUser(toAuthUser(session))
      })
      .catch(() => clearApiSession())
      .finally(() => {
        if (mounted) setInitializing(false)
      })
    return () => {
      mounted = false
    }
  }, [])

  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    if (!email.trim() || !password.trim()) {
      return { ok: false, error: 'Email and password are required.' }
    }
    try {
      const session = await loginRequest(email.trim(), password)
      setUser(toAuthUser(session))
      navigate('/dashboard')
      return { ok: true }
    } catch (error) {
      clearApiSession()
      return { ok: false, error: loginError(error) }
    }
  }, [])

  const logout = useCallback(async (): Promise<void> => {
    try {
      await logoutRequest()
    } finally {
      setUser(null)
      navigate('/login')
    }
  }, [])

  const value = useMemo(
    () => ({ user, initializing, login, logout }),
    [user, initializing, login, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
